import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Message, Tab } from 'semantic-ui-react'
import styled from 'styled-components'
import { FlexRowBetween, LoaderBar, PageHeader, PaginationComponent } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { useAuth } from 'context'
import { isApprovalAdmin, isRoleModelOn } from 'utility/role-model'
import { AutoTicketService } from 'utility/services/auto-ticket'
import { TicketInspectionGroups } from './TicketInspectionGroups'
import {
    TICKET_VIEWS,
    assigneeLabel,
    groupTicketsByInspection,
    isOpenTicketStatus,
    normalizeTicketInspections,
    orderTicketsByInspection,
    readTicketView,
    saveTicketView,
} from './ticket-inspection-utils'
import {
    TICKET_STATUS,
    formatTicketDateTime,
    getApiErrorMessage,
    normalizeTicketDetail,
    normalizeTicketListResponse,
    ticketMatchesSearch,
} from './utils'
import { TicketPreviewEye, TicketQuickPreview } from './TicketQuickPreview'
import {
    SearchInput,
    TicketCountText,
    TicketMobileCard,
    TicketMobileFooter,
    TicketMobileId,
    TicketMobileList,
    TicketMobileMeta,
    TicketMobileTitle,
    TicketMobileTop,
    TicketStatusPill,
    TicketTable,
    TicketTableScroll,
    TicketTableWrap,
    TipCard,
    ToolbarRow,
} from 'page-components/auto-ticket-rules/styles'

const PAGE_SIZE = 25
// "By inspection" loads a bigger page so the findings of one inspection stay together
const GROUP_PAGE_SIZE = 100

const CenteredTicketTable = styled(TicketTable)`
    && th,
    && td.align-center {
        text-align: center;
    }
    && td.insp {
        text-align: left;
        min-width: 180px;
    }
    && td.insp .sub {
        display: block;
        color: #6b7280;
        font-size: 12px;
    }
    && tr.first-of-inspection td {
        border-top: 2px solid #e5e7eb;
    }
`

const ViewSwitch = styled.div`
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    overflow: hidden;

    button {
        border: 0;
        background: #fff;
        color: #6b7280;
        padding: 7px 12px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
    }
    button[aria-pressed='true'] {
        background: #eef2ff;
        color: #1d4ed8;
    }
`

const FilterSelect = styled.select`
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    max-width: 220px;
`

const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;

    @media (max-width: 767px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    div {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 10px 14px;
    }
    b {
        display: block;
        font-size: 20px;
    }
    span {
        color: #6b7280;
        font-size: 12px;
    }
`

const TABS = [
    { id: 'active', label: 'Active', status: '1,2,3,6,7' },
    {
        id: 'waiting',
        label: 'Waiting approval',
        status: '8',
        approversOnly: true,
    },
    { id: 'done', label: 'Done', status: '4,5,9' },
    { id: 'all', label: 'All', status: '' },
]

const statusStyle = statusId => {
    const meta = TICKET_STATUS[statusId] || TICKET_STATUS[1]
    const map = {
        open: { bg: '#f1f5f9', color: '#475569' },
        ack: { bg: '#dbeafe', color: '#1d4ed8' },
        progress: { bg: '#fef3c7', color: '#b45309' },
        resolved: { bg: '#dcfce7', color: '#15803d' },
        closed: { bg: '#f4f4f5', color: '#52525b' },
        escalated: { bg: '#ffedd5', color: '#c2410c' },
        hold: { bg: '#f3e8ff', color: '#6d28d9' },
        pending: { bg: '#e0e7ff', color: '#3730a3' },
        cancelled: { bg: '#f4f4f5', color: '#52525b' },
    }
    return map[meta.pill] || map.open
}

const renderStatusPill = ticket => {
    const statusMeta = TICKET_STATUS[ticket.statusId] || TICKET_STATUS[1]
    const st = statusStyle(ticket.statusId)
    return (
        <TicketStatusPill $bg={st.bg} $color={st.color}>
            {statusMeta.label}
        </TicketStatusPill>
    )
}

const TicketRows = ({
    tickets,
    onOpen,
    onPreview,
    byAudit = {},
    assigneeNames = {},
}) => (
    <>
        <DesktopMode>
            <TicketTableScroll>
                <CenteredTicketTable>
                    <thead>
                        <tr>
                            <th>Ticket</th>
                            <th>Title</th>
                            <th>Inspection</th>
                            <th>Assigned to</th>
                            <th>Status</th>
                            <th>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.map((ticket, index) => {
                            const statusMeta =
                                TICKET_STATUS[ticket.statusId] || TICKET_STATUS[1]
                            const st = statusStyle(ticket.statusId)
                            const insp = byAudit[ticket.auditId]
                            const firstOfInspection =
                                index > 0 &&
                                tickets[index - 1].auditId !== ticket.auditId
                            return (
                                <tr
                                    key={ticket.ticketId}
                                    className={
                                        firstOfInspection
                                            ? 'first-of-inspection'
                                            : undefined
                                    }
                                    onClick={() => onOpen(ticket.ticketId)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>
                                        <span className="mono">
                                            #{ticket.ticketId}
                                        </span>
                                    </td>
                                    <td className="title-cell">
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <span>{ticket.title || '—'}</span>
                                            <TicketPreviewEye
                                                onClick={e =>
                                                    onPreview(e, ticket)
                                                }
                                            />
                                        </span>
                                    </td>
                                    <td className="insp">
                                        {insp?.auditName ||
                                            ticket.auditName ||
                                            ticket.templateName ||
                                            '—'}
                                        <span className="sub">
                                            {[
                                                insp?.locationName,
                                                insp?.templateName ||
                                                    ticket.templateName,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </span>
                                    </td>
                                    <td className="align-center">
                                        {assigneeLabel(ticket, assigneeNames)}
                                    </td>
                                    <td className="align-center">
                                        <TicketStatusPill
                                            $bg={st.bg}
                                            $color={st.color}
                                        >
                                            {statusMeta.label}
                                        </TicketStatusPill>
                                    </td>
                                    <td className="align-center">
                                        {formatTicketDateTime(ticket.createdOn)}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </CenteredTicketTable>
            </TicketTableScroll>
        </DesktopMode>
        <MobileMode>
            <TicketMobileList>
                {tickets.map(ticket => {
                    const statusMeta =
                        TICKET_STATUS[ticket.statusId] || TICKET_STATUS[1]
                    const st = statusStyle(ticket.statusId)
                    return (
                        <TicketMobileCard
                            key={ticket.ticketId}
                            as="div"
                            role="button"
                            tabIndex={0}
                            onClick={() => onOpen(ticket.ticketId)}
                            onKeyDown={event => {
                                if (event.key !== 'Enter' && event.key !== ' ') {
                                    return
                                }
                                event.preventDefault()
                                onOpen(ticket.ticketId)
                            }}
                        >
                            <TicketMobileTop>
                                <TicketMobileId>
                                    #{ticket.ticketId}
                                </TicketMobileId>
                            </TicketMobileTop>
                            <TicketMobileMeta>
                                <TicketStatusPill $bg={st.bg} $color={st.color}>
                                    {statusMeta.label}
                                </TicketStatusPill>
                            </TicketMobileMeta>
                            <TicketMobileTitle>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}
                                >
                                    <span>{ticket.title || '—'}</span>
                                    <TicketPreviewEye
                                        onClick={e => onPreview(e, ticket)}
                                    />
                                </span>
                            </TicketMobileTitle>
                            <TicketMobileFooter>
                                <div>
                                    <span className="label">Inspection</span>
                                    <span className="value">
                                        {byAudit[ticket.auditId]?.auditName ||
                                            ticket.auditName ||
                                            ticket.templateName ||
                                            '—'}
                                    </span>
                                </div>
                                <div>
                                    <span className="label">Created</span>
                                    <span className="value">
                                        {formatTicketDateTime(ticket.createdOn)}
                                    </span>
                                </div>
                            </TicketMobileFooter>
                        </TicketMobileCard>
                    )
                })}
            </TicketMobileList>
        </MobileMode>
    </>
)

export const TicketsListPage = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    // tickets held for report approval: company admins (login CanApproveReports also covers workflow approvers now)
    const canSeeWaiting = useMemo(() => isApprovalAdmin(user), [user])
    const tabs = useMemo(
        () => TABS.filter(tab => !tab.approversOnly || canSeeWaiting),
        [canSeeWaiting],
    )
    const [activeIndex, setActiveIndex] = useState(0)
    const [page, setPage] = useState(1)
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [search, setSearch] = useState('')
    // 2026-10-22 "By inspection" view (default) / plain list, remembered per browser
    const [view, setView] = useState(readTicketView)
    const [byAudit, setByAudit] = useState({})
    const [assigneeNames, setAssigneeNames] = useState({})
    const [locationFilter, setLocationFilter] = useState('')
    const [assigneeFilter, setAssigneeFilter] = useState('')
    const locationLabel = isRoleModelOn(user) ? 'Department' : 'Site'
    const pageSize = view === TICKET_VIEWS.group ? GROUP_PAGE_SIZE : PAGE_SIZE
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [forbidden, setForbidden] = useState(false)
    const [previewTicket, setPreviewTicket] = useState(null)
    const [previewAnchor, setPreviewAnchor] = useState(null)
    const [previewDetail, setPreviewDetail] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewError, setPreviewError] = useState('')
    const previewRequestRef = useRef(0)

    const activeTab = tabs[activeIndex] || tabs[0]

    const load = useCallback(async () => {
        if (!activeTab) return
        setLoading(true)
        setError('')
        setForbidden(false)
        try {
            const params = {
                pageNumber: page,
                pageSize,
                includeInspections: true,
            }
            if (activeTab.status) params.status = activeTab.status
            const res = await AutoTicketService.getTickets(params)
            const normalized = normalizeTicketListResponse(res)
            const summaries = normalizeTicketInspections(res)
            setItems(normalized.items)
            setTotal(normalized.totalCount)
            setByAudit(summaries.byAudit)
            setAssigneeNames(summaries.assigneeNames)
        } catch (err) {
            setItems([])
            setTotal(0)
            setByAudit({})
            setAssigneeNames({})
            if (err?.status === 403) {
                setForbidden(true)
            } else {
                setError(getApiErrorMessage(err))
            }
        } finally {
            setLoading(false)
        }
    }, [activeTab, page, pageSize])

    useEffect(() => {
        load()
    }, [load])

    const filtered = useMemo(() => {
        const query = String(search || '')
            .trim()
            .toLowerCase()
        return items.filter(ticket => {
            const insp = byAudit[ticket.auditId]
            if (
                locationFilter &&
                String(insp?.locationName || '') !== locationFilter
            ) {
                return false
            }
            if (
                assigneeFilter &&
                assigneeLabel(ticket, assigneeNames) !== assigneeFilter
            ) {
                return false
            }
            if (!query) return true
            const extra = [
                ticket.templateName,
                insp?.auditName || ticket.auditName,
                insp?.locationName,
            ]
                .join(' ')
                .toLowerCase()
            return ticketMatchesSearch(ticket, search) || extra.includes(query)
        })
    }, [items, search, byAudit, assigneeNames, locationFilter, assigneeFilter])

    const locationOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    Object.values(byAudit)
                        .map(i => i.locationName)
                        .filter(Boolean),
                ),
            ).sort(),
        [byAudit],
    )
    const assigneeOptions = useMemo(
        () =>
            Array.from(
                new Set(items.map(t => assigneeLabel(t, assigneeNames))),
            )
                .filter(name => name && name !== '—')
                .sort(),
        [items, assigneeNames],
    )

    const groups = useMemo(
        () => groupTicketsByInspection(filtered, byAudit),
        [filtered, byAudit],
    )
    const listRows = useMemo(() => orderTicketsByInspection(filtered), [filtered])

    const kpis = useMemo(() => {
        const auditIds = new Set(filtered.map(t => t.auditId).filter(Boolean))
        return {
            open: filtered.filter(t => isOpenTicketStatus(t.statusId)).length,
            resolved: filtered.filter(t => Number(t.statusId) === 4).length,
            inspections: auditIds.size,
            waiting: Array.from(auditIds).filter(
                id => byAudit[id]?.approvalState === 'waiting_findings',
            ).length,
        }
    }, [filtered, byAudit])

    // filters work on the loaded page: a new page / view starts unfiltered (review 2026-10-22)
    const resetFilters = () => {
        setLocationFilter('')
        setAssigneeFilter('')
    }

    const changeView = next => {
        setView(next)
        saveTicketView(next)
        setPage(1)
        resetFilters()
    }

    // the numbers cover the loaded page; say so when the tab has more tickets than one page
    const kpiScope = total > items.length ? ' (this page)' : ''
    const hasApprovalWorkflow = Object.values(byAudit).some(
        i => i.approvalState && i.approvalState !== 'none',
    )

    const openTicket = ticketId => navigate(`/app/ticket/${ticketId}`)

    const closePreview = useCallback(() => {
        setPreviewTicket(null)
        setPreviewAnchor(null)
        setPreviewDetail(null)
        setPreviewLoading(false)
        setPreviewError('')
    }, [])

    const openPreview = useCallback((event, ticket) => {
        event.stopPropagation()
        const anchor = event.currentTarget
        setPreviewTicket(ticket)
        setPreviewAnchor(anchor)
        setPreviewDetail(null)
        setPreviewError('')
        setPreviewLoading(true)
        const requestId = previewRequestRef.current + 1
        previewRequestRef.current = requestId
        AutoTicketService.getDetail(ticket.ticketId)
            .then(res => {
                if (previewRequestRef.current !== requestId) return
                setPreviewDetail(normalizeTicketDetail(res))
            })
            .catch(err => {
                if (previewRequestRef.current !== requestId) return
                setPreviewDetail(null)
                setPreviewError(
                    getApiErrorMessage(err) ||
                        'Could not load ticket details.',
                )
            })
            .finally(() => {
                if (previewRequestRef.current !== requestId) return
                setPreviewLoading(false)
            })
    }, [])

    const panes = tabs.map(tab => ({
        menuItem: tab.label,
        render: () => (
            <div>
                {loading && <LoaderBar content="Loading tickets…" />}
                {!loading && forbidden && (
                    <TipCard>
                        <h2>Waiting approval</h2>
                        <p>
                            This list is available to admins and approvers.
                        </p>
                    </TipCard>
                )}
                {!loading && !forbidden && (
                    <>
                        <ToolbarRow style={{ flexWrap: 'wrap' }}>
                            <SearchInput
                                type="text"
                                placeholder={`Search by ticket #, title, inspection or ${locationLabel.toLowerCase()}…`}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            {(locationOptions.length > 0 || locationFilter) && (
                                <FilterSelect
                                    aria-label={locationLabel}
                                    value={locationFilter}
                                    onChange={e =>
                                        setLocationFilter(e.target.value)
                                    }
                                >
                                    <option value="">
                                        All {locationLabel.toLowerCase()}s
                                    </option>
                                    {locationOptions.map(name => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                </FilterSelect>
                            )}
                            {(assigneeOptions.length > 1 || assigneeFilter) && (
                                <FilterSelect
                                    aria-label="Assigned to"
                                    value={assigneeFilter}
                                    onChange={e =>
                                        setAssigneeFilter(e.target.value)
                                    }
                                >
                                    <option value="">Anyone</option>
                                    {assigneeOptions.map(name => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                </FilterSelect>
                            )}
                            <div className="spacer" />
                            <TicketCountText>
                                {filtered.length} of {total} ticket
                                {total === 1 ? '' : 's'}
                            </TicketCountText>
                        </ToolbarRow>
                        {filtered.length > 0 && (
                            <Kpis>
                                <div>
                                    <b>{kpis.open}</b>
                                    <span>Open findings{kpiScope}</span>
                                </div>
                                <div>
                                    <b>{kpis.resolved}</b>
                                    <span>Resolved, not closed{kpiScope}</span>
                                </div>
                                <div>
                                    <b>{kpis.inspections}</b>
                                    <span>Inspections with findings{kpiScope}</span>
                                </div>
                                {hasApprovalWorkflow && (
                                    <div>
                                        <b>{kpis.waiting}</b>
                                        <span>
                                            Reports waiting for their findings
                                            {kpiScope}
                                        </span>
                                    </div>
                                )}
                            </Kpis>
                        )}
                        {filtered.length === 0 && (
                            <TipCard>
                                <h2>
                                    {items.length === 0
                                        ? 'No tickets yet'
                                        : 'No matches'}
                                </h2>
                                <p>
                                    {items.length === 0
                                        ? 'No tickets in this list.'
                                        : 'No tickets match your search.'}
                                </p>
                            </TipCard>
                        )}
                        {filtered.length > 0 && view === TICKET_VIEWS.group && (
                            <TicketInspectionGroups
                                groups={groups}
                                locationLabel={locationLabel}
                                assigneeNames={assigneeNames}
                                onOpen={openTicket}
                                onPreview={openPreview}
                                renderStatus={renderStatusPill}
                            />
                        )}
                        {filtered.length > 0 && view === TICKET_VIEWS.list && (
                            <TicketTableWrap>
                                <TicketRows
                                    tickets={listRows}
                                    onOpen={openTicket}
                                    onPreview={openPreview}
                                    byAudit={byAudit}
                                    assigneeNames={assigneeNames}
                                />
                            </TicketTableWrap>
                        )}
                        {total > pageSize && (
                            <div className="w-100 justify-content-end d-flex mt-3">
                                <PaginationComponent
                                    activePage={page}
                                    pageChanged={nextPage => {
                                        setPage(nextPage)
                                        resetFilters()
                                    }}
                                    pageSize={pageSize}
                                    totalCount={total}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        ),
    }))

    return (
        <>
            <FlexRowBetween className="mb-3">
                <PageHeader header="Tickets" />
                <ViewSwitch role="group" aria-label="View">
                    <button
                        type="button"
                        aria-pressed={view === TICKET_VIEWS.group}
                        onClick={() => changeView(TICKET_VIEWS.group)}
                    >
                        By inspection
                    </button>
                    <button
                        type="button"
                        aria-pressed={view === TICKET_VIEWS.list}
                        onClick={() => changeView(TICKET_VIEWS.list)}
                    >
                        List
                    </button>
                </ViewSwitch>
            </FlexRowBetween>
            {error && <Message negative>{error}</Message>}
            <Tab
                menu={{ secondary: true, pointing: true }}
                panes={panes}
                activeIndex={activeIndex}
                onTabChange={(_, { activeIndex: nextIndex }) => {
                    setActiveIndex(nextIndex)
                    setPage(1)
                    setSearch('')
                    setLocationFilter('')
                    setAssigneeFilter('')
                    closePreview()
                }}
            />
            <TicketQuickPreview
                ticket={previewTicket}
                detail={previewDetail}
                loading={previewLoading}
                error={previewError}
                anchorEl={previewAnchor}
                open={Boolean(previewTicket)}
                onClose={closePreview}
                onView={openTicket}
            />
        </>
    )
}
